import { offset, flip, autoUpdate, useRole, useClick, useHover, useDismiss, useFloating, useInteractions, useListNavigation, safePolygon, } from '@floating-ui/react';
import { useState, useMemo, useRef, createContext, useContext } from 'react';
export const useDropdown = ({ placement = 'bottom', open: controlledOpen, onOpenChange: setControlledOpen, trigger = 'click', offsetOptions, flipOptions, } = {}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isOpen = controlledOpen ?? uncontrolledOpen;
    const setIsOpen = setControlledOpen ?? setUncontrolledOpen;
    const [activeIndex, setActiveIndex] = useState(null);
    const listItemsRef = useRef([]);
    const floatingData = useFloating({
        placement,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        middleware: [!!offsetOptions && offset(offsetOptions), flipOptions && flip(flipOptions)],
    });
    const { context } = floatingData;
    const click = useClick(context, {
        event: 'mousedown',
        enabled: trigger === 'click' && controlledOpen === undefined,
    });
    const hover = useHover(context, {
        enabled: trigger === 'hover' && controlledOpen === undefined,
        delay: { open: 100 },
        handleClose: safePolygon({ blockPointerEvents: true }),
    });
    const role = useRole(context, { role: 'menu' });
    const dismiss = useDismiss(context);
    const listNavigation = useListNavigation(context, {
        listRef: listItemsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        focusItemOnOpen: false,
        focusItemOnHover: false,
    });
    const interactions = useInteractions([hover, click, role, dismiss, listNavigation]);
    return useMemo(() => ({
        isOpen,
        setIsOpen,
        ...interactions,
        ...floatingData,
        listItemsRef,
        activeIndex,
        setActiveIndex,
    }), [isOpen, setIsOpen, interactions, floatingData, listItemsRef, activeIndex]);
};
export const DropdownContext = createContext(null);
export const useDropdownContext = () => {
    const context = useContext(DropdownContext);
    if (context == null) {
        throw new Error('Dropdown components must be wrapped in <Dropdown />');
    }
    return context;
};
